import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useThemeMode } from '../../design-system/theme';
import useABTest from '../../hooks/useABTest';
import { isPreviewExperimentOverrideHost } from '../../utils/experimentOverrideHosts';
import {
  EducationDesignPreview,
  normalizeEducationDesignVariant,
} from '../education/EducationDesignExperiment';
import NextChallengePage from './NextChallengePage';
import {
  CHALLENGE_CLARITY_HOME_EXPERIMENT,
  type ChallengeExperimentContext,
} from './challengePageExperiment';
import './ChallengePageClarityHome.css';

// The JavaScript hook exposes this stable contract; its legacy JSDoc returns Object.
// Keep the boundary explicit, as in EducationDesignExperiment.
type ChallengeAssignment = {
  variant: string | boolean | null;
  isLoading: boolean;
  isExperimentActive: boolean;
  isPreviewOverride: boolean;
};

const normalizeDesign = (value: string | boolean | null): 'control' | 'test' | null =>
  value === 'control' || value === 'test' ? value : null;

/** A new assignment replaces the old design wrapper atomically, never its history. */
export default function ChallengeClarityHomeExperiment({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const location = useLocation();
  const [theme] = useThemeMode();
  const experiment = CHALLENGE_CLARITY_HOME_EXPERIMENT;
  const isTarget = location.pathname.replace(/\/+$/, '') === '/challenges';
  const preview =
    isTarget && isPreviewExperimentOverrideHost()
      ? normalizeDesign(new URLSearchParams(location.search).get('challengeDesign'))
      : null;
  const trackingData = useMemo(
    () => ({
      page: location.pathname,
      challenge_path: location.pathname,
      surface: 'challenge_landing_page',
      education_theme: theme,
      validVariants: experiment.posthogVariants,
      skipFeatureFlagEvaluation: !isTarget || Boolean(preview),
      forcedVariant: !isTarget ? 'control' : preview || undefined,
      trackForcedExposure: false,
    }),
    [location.pathname, theme, experiment.posthogVariants, isTarget, preview],
  );
  // SAFETY: useABTest returns these four fields on every path; the JS hook
  // normalizes variants to string, boolean, or null and supplies boolean state.
  const { variant, isLoading, isExperimentActive, isPreviewOverride } = useABTest(
    experiment.name,
    experiment.featureFlag,
    'control',
    trackingData,
  ) as ChallengeAssignment;
  if (!isTarget) return <>{children}</>;
  // Do not mount the old experiment while the new assignment is unresolved.
  if (isLoading && !preview) {
    return (
      <div className="chp-gate-loading" role="status">
        Loading challenge…
      </div>
    );
  }
  const assigned = preview || (isExperimentActive ? normalizeDesign(variant) : null);
  if (!assigned) return <>{children}</>;
  // A control preview on localhost or Dev still has to honor educationDesign.
  // Live control keeps the replacement page. Clarity Home stays the test arm.
  const educationDesignPreview =
    preview === 'control'
      ? normalizeEducationDesignVariant(new URLSearchParams(location.search).get('educationDesign'))
      : null;
  if (educationDesignPreview) return <>{children}</>;
  const active = Boolean(isExperimentActive && !preview && !isPreviewOverride);
  const featureProperty = `$feature/${experiment.featureFlag}` as const;
  const experimentContext: ChallengeExperimentContext | undefined = active
    ? {
        experiment_name: experiment.name,
        experiment_variant: assigned,
        feature_flag: experiment.featureFlag,
        [featureProperty]: assigned,
        is_experiment_active: true,
      }
    : undefined;
  return (
    <div
      className={`education-design-experiment${assigned === 'test' ? ' challenge-home-review' : ''}`}
      data-testid="challenge-clarity-home-experiment"
      data-challenge-design={assigned}
      data-experiment-active={String(active)}
      data-education-theme={theme}
      data-bs-theme={theme}
    >
      <EducationDesignPreview variant="control">
        <NextChallengePage
          presentation={assigned === 'test' ? 'clarity-home' : undefined}
          experimentContext={experimentContext}
        />
      </EducationDesignPreview>
    </div>
  );
}
